/**
 * Helpers for running inside the Twine iOS / Android app (apps/mobile).
 *
 * The app is a Capacitor shell that loads the live site, so there are two
 * ways to know we're in it:
 * - The user agent carries `TwineApp/<platform>` (set by appendUserAgent in
 *   capacitor.config.ts). Works on the server, so SSR can render app mode on
 *   first paint. The server seeds redux/modules/nativeApp from it.
 * - On the client, Capacitor injects `window.Capacitor` with the native bridge.
 *
 * We call native plugins through the injected bridge directly rather than
 * bundling @capacitor/core: plugin calls are just
 * `Capacitor.nativePromise(plugin, method, options)`, and plugins only exist
 * if the native project includes them (checked via PluginHeaders).
 */

const NATIVE_APP_UA = /\bTwineApp\/(ios|android)\b/;

const PREFERENCE_FALLBACK_PREFIX = 'twine-app:';

export function detectNativePlatform(userAgent) {
  const match = NATIVE_APP_UA.exec(userAgent || '');
  return match ? match[1] : null;
}

function getBridge() {
  if (typeof window === 'undefined') return null;
  const cap = window.Capacitor;
  return cap && typeof cap.nativePromise === 'function' ? cap : null;
}

export function hasNativePlugin(name) {
  const bridge = getBridge();
  return Boolean(
    bridge &&
      Array.isArray(bridge.PluginHeaders) &&
      bridge.PluginHeaders.some((header) => header.name === name)
  );
}

export function callNative(plugin, method, options = {}) {
  if (!hasNativePlugin(plugin)) {
    return Promise.reject(new Error(`${plugin} plugin unavailable`));
  }
  return getBridge().nativePromise(plugin, method, options);
}

/** Returns a handle with remove(), or null when the plugin is unavailable. */
export function addNativeListener(plugin, eventName, callback) {
  if (!hasNativePlugin(plugin)) return null;
  return getBridge().addListener(plugin, eventName, callback);
}

export function hideSplashScreen() {
  return callNative('SplashScreen', 'hide').catch(() => {});
}

/**
 * Device-local app settings. Native Preferences survive WebView storage
 * eviction; localStorage is the fallback when the plugin is missing.
 */
export async function getAppPreference(key) {
  if (hasNativePlugin('Preferences')) {
    try {
      const { value } = await callNative('Preferences', 'get', { key });
      return value;
    } catch (e) {
      // Fall through to localStorage
    }
  }
  try {
    return window.localStorage.getItem(PREFERENCE_FALLBACK_PREFIX + key);
  } catch (e) {
    return null;
  }
}

export async function setAppPreference(key, value) {
  if (hasNativePlugin('Preferences')) {
    try {
      await callNative('Preferences', 'set', { key, value: String(value) });
      return;
    } catch (e) {
      // Fall through to localStorage
    }
  }
  try {
    window.localStorage.setItem(PREFERENCE_FALLBACK_PREFIX + key, value);
  } catch (e) {
    // Storage unavailable; the setting just won't persist.
  }
}

export const PUSH_PERMISSION = {
  GRANTED: 'granted',
  DENIED: 'denied',
  UNAVAILABLE: 'unavailable',
};

/**
 * Asks the OS for notification permission. Push delivery itself (FCM) is
 * TP-388 W3 — until its plugin ships in the native build this resolves
 * UNAVAILABLE, and callers just record the user's choice.
 */
export async function requestPushPermission() {
  if (!hasNativePlugin('PushNotifications')) {
    return PUSH_PERMISSION.UNAVAILABLE;
  }
  try {
    const { receive } = await callNative(
      'PushNotifications',
      'requestPermissions'
    );
    return receive === 'granted'
      ? PUSH_PERMISSION.GRANTED
      : PUSH_PERMISSION.DENIED;
  } catch (e) {
    return PUSH_PERMISSION.UNAVAILABLE;
  }
}
